import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { Card, Button, Input, Table, Space, message, Select, Modal, Dropdown } from 'antd';
import { 
  PlusOutlined, 
  ImportOutlined,
  FileExcelOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  DownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import PermissionGate from '../../components/PermissionGate';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { useListReturnState } from '../../hooks/useListReturnState';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { productsApi } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../constants/ui';

const { Option } = Select;

const Products = () => {
  const navigate = useNavigate();
  const { canEditRecord, canDeleteRecord } = useResourcePermissions('products');
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    category_id: 'all',
    sub_category_id: 'all',
    unit_type_id: 'all',
    search: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    subCategories: [],
    unitTypes: [],
  });
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [filters, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  // دریافت محصولات از API
  useEffect(() => {
    fetchProducts();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {
        category_id: filters.category_id === 'all' ? undefined : filters.category_id,
        sub_category_id: filters.sub_category_id === 'all' ? undefined : filters.sub_category_id,
        unit_type_id: filters.unit_type_id === 'all' ? undefined : filters.unit_type_id,
        search: filters.search || undefined,
        page: pagination.current,
        per_page: pagination.pageSize,
      };

      const res = await productsApi.getAll(params);
      
      console.log('📥 Products API response:', res);
      
      // بررسی پاسخ API - ممکن است status نداشته باشد
      if (res?.products) {
        setProducts(res.products || []);
        setPagination(prev => ({
          ...prev,
          total: res?.pagination?.total || 0,
        }));

        // ذخیره فیلترها
        if (res?.filters) {
          setFilterOptions({
            categories: res.filters.categories || [],
            subCategories: res.filters.subCategories || [],
            unitTypes: res.filters.unitTypes || [],
          });
        }
      } else if (res?.data?.products) {
        // حالت جایگزین - اگر داده‌ها در data باشد
        setProducts(res.data.products || []);
        setPagination(prev => ({
          ...prev,
          total: res?.data?.pagination?.total || 0,
        }));

        if (res?.data?.filters) {
          setFilterOptions({
            categories: res.data.filters.categories || [],
            subCategories: res.data.filters.subCategories || [],
            unitTypes: res.data.filters.unitTypes || [],
          });
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('خطا در دریافت محصولات');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    navigate('/products/add');
  };

  const handleImport = () => {
    message.info('قابلیت وارد کردن در حال توسعه است');
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await productsApi.exportExcel({
        category_id: filters.category_id || 'all',
        sub_category_id: filters.sub_category_id || 'all',
        unit_type_id: filters.unit_type_id || 'all',
      });
      message.success('خروجی اکسل در حال دانلود است');
    } catch (error) {
      console.error('Export products:', error);
      message.error(error?.message || 'خطا در خروجی اکسل');
    } finally {
      setExporting(false);
    }
  };

  const handleEdit = (record) => {
    navigateToDetail(`/products/${record.id}/edit`);
  };

  const handleView = (record) => {
    navigateToDetail(`/products/${record.id}`, {}, {
      listPath: '/products',
      listLabel: 'محصولات',
      entityLabel: record.name || `محصول #${record.id}`,
    });
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'حذف محصول',
      content: 'آیا از حذف این محصول اطمینان دارید؟',
      okText: 'بله، حذف کن',
      cancelText: 'لغو',
      okButtonProps: { danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } },
      cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
      onOk: async () => {
        try {
          await productsApi.delete(id);
          message.success('محصول با موفقیت حذف شد');
          fetchProducts();
        } catch (error) {
          console.error('Error deleting product:', error);
          message.error('خطا در حذف محصول');
        }
      },
    });
  };

  // عملیات گروهی
  const handleBulkAction = async (actionType, status = null) => {
    if (selectedRowKeys.length === 0) {
      message.warning('لطفاً حداقل یک محصول را انتخاب کنید');
      return;
    }

    const actionTexts = {
      'delete': 'حذف',
      'change-purchase': status === 1 ? 'فعال‌سازی امکان خرید' : 'غیرفعال‌سازی امکان خرید',
    };

    Modal.confirm({
      title: `${actionTexts[actionType]} محصولات`,
      content: `آیا از ${actionTexts[actionType]} ${selectedRowKeys.length} محصول انتخاب شده اطمینان دارید؟`,
      okText: 'بله، انجام بده',
      cancelText: 'لغو',
      okButtonProps: actionType === 'delete' 
        ? { danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } }
        : { type: 'primary', style: { backgroundColor: '#1890ff', borderColor: '#1890ff', color: '#fff' } },
      cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
      onOk: async () => {
        setBulkActionLoading(true);
        try {
          await productsApi.applyQuickAction(actionType, selectedRowKeys, status);
          message.success(`عملیات ${actionTexts[actionType]} با موفقیت انجام شد`);
          setSelectedRowKeys([]);
          fetchProducts();
        } catch (error) {
          console.error('Error applying bulk action:', error);
          message.error('خطا در انجام عملیات');
        } finally {
          setBulkActionLoading(false);
        }
      },
    });
  };

  // منوی عملیات گروهی
  const bulkActionMenuItems = [
    {
      key: 'delete',
      label: 'حذف محصولات',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleBulkAction('delete'),
    },
    {
      type: 'divider',
    },
    {
      key: 'enable-purchase',
      label: 'فعال‌سازی امکان خرید',
      icon: <CheckCircleOutlined className="text-green-600" />,
      onClick: () => handleBulkAction('change-purchase', 1),
    },
    {
      key: 'disable-purchase',
      label: 'غیرفعال‌سازی امکان خرید',
      icon: <CloseCircleOutlined className="text-red-600" />,
      onClick: () => handleBulkAction('change-purchase', 0),
    },
  ];

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setPagination(prev => ({
      ...prev,
      current: 1, // Reset to first page when filter changes
    }));
  };

  const handleSearch = (value) => {
    setFilters(prev => ({
      ...prev,
      search: value,
    }));
    setPagination(prev => ({
      ...prev,
      current: 1,
    }));
  };

  const columns = [
    {
      title: 'نام محصول',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => (
        <span style={{ fontFamily: 'CustomFont', fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: 'قیمت',
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0),
      render: (price) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {formatPriceToman(price || 0)}
        </span>
      ),
    },
    {
      title: 'دسته‌بندی',
      dataIndex: 'category',
      key: 'category',
      render: (_, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {record.category?.category_name || '--'}
        </span>
      ),
    },
    {
      title: 'زیردسته‌بندی',
      dataIndex: 'sub_category',
      key: 'sub_category',
      render: (_, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {record.sub_category?.category_name || '--'}
        </span>
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            style={{ fontFamily: 'CustomFont' }}
            title="مشاهده"
          />
          {canEditRecord(record) && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              style={{ fontFamily: 'CustomFont' }}
              title="ویرایش"
            />
          )}
          {canDeleteRecord(record) && (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
              className="hover:bg-red-50 dark:hover:bg-red-900/20"
              style={{ fontFamily: 'CustomFont' }}
              title="حذف"
            />
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        {/* Main Container Card */}
        <Card 
          className="shadow-xl border-0"
          bodyStyle={{ padding: 0 }}
        >
          {/* Header Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                  <PlusOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
                </div>
                <div>
                  <h1 
                    className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    مدیریت محصولات
                  </h1>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                <PermissionGate permission="add_product">
                <Button
                  icon={<ImportOutlined />}
                  onClick={handleImport}
                  size="middle"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  وارد کردن
                </Button>
                </PermissionGate>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  loading={exporting}
                  size="middle"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  خروجی اکسل
                </Button>
                <PermissionGate permission="add_product">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddProduct}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg transition-all"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  افزودن محصول
                </Button>
                </PermissionGate>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={5} columns={7} />
            ) : (
              <>
            {/* Filters and Search Bar */}
            <div className="mb-6 flex flex-wrap gap-3 items-center">
              <Input
                placeholder="جستجو در محصولات (نام، SKU، دسته‌بندی)..."
                allowClear
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                onPressEnter={(e) => handleSearch(e.target.value)}
                style={{ fontFamily: 'CustomFont', maxWidth: '300px', width: '100%' }}
                size="middle"
                prefix={<SearchOutlined className="text-gray-400" />}
                className="rounded-lg"
              />
              
              <Select
                placeholder="دسته‌بندی"
                allowClear
                value={filters.category_id === 'all' ? undefined : filters.category_id}
                onChange={(value) => handleFilterChange('category_id', value || 'all')}
                style={{ fontFamily: 'CustomFont', minWidth: '150px' }}
              >
                <Option value="all">همه</Option>
                {filterOptions.categories.map(cat => (
                  <Option key={cat.id} value={cat.id}>{cat.category_name}</Option>
                ))}
              </Select>

              <Select
                placeholder="زیردسته‌بندی"
                allowClear
                value={filters.sub_category_id === 'all' ? undefined : filters.sub_category_id}
                onChange={(value) => handleFilterChange('sub_category_id', value || 'all')}
                style={{ fontFamily: 'CustomFont', minWidth: '150px' }}
                disabled={filters.category_id === 'all'}
              >
                <Option value="all">همه</Option>
                {filterOptions.subCategories.map(sub => (
                  <Option key={sub.id} value={sub.id}>{sub.category_name}</Option>
                ))}
              </Select>

              <Select
                placeholder="نوع واحد"
                allowClear
                value={filters.unit_type_id === 'all' ? undefined : filters.unit_type_id}
                onChange={(value) => handleFilterChange('unit_type_id', value || 'all')}
                style={{ fontFamily: 'CustomFont', minWidth: '150px' }}
              >
                <Option value="all">همه</Option>
                {filterOptions.unitTypes.map(unit => (
                  <Option key={unit.id} value={unit.id}>{unit.unit_type}</Option>
                ))}
              </Select>

              <Button
                icon={<ReloadOutlined />}
                onClick={fetchProducts}
                style={{ fontFamily: 'CustomFont' }}
              >
                به‌روزرسانی
              </Button>
            </div>

            {/* عملیات گروهی */}
            {selectedRowKeys.length > 0 && (
              <div className={BULK_ACTION_BAR_CLASS}>
                <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
                  {selectedRowKeys.length} مورد انتخاب شده
                </span>
                <Dropdown
                  menu={{ items: bulkActionMenuItems }}
                  trigger={['click']}
                >
                  <Button
                    type="primary"
                    loading={bulkActionLoading}
                    size="middle"
                    className="border-0"
                    icon={<DownOutlined />}
                    style={{ fontFamily: 'CustomFont', backgroundColor: '#f97316', color: '#fff' }}
                  >
                    عملیات گروهی
                  </Button>
                </Dropdown>
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
              <Table
                columns={columns}
                dataSource={products}
                rowKey="id"
                rowSelection={rowSelection}
                loading={loading}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => (
                    <span style={{ fontFamily: 'CustomFont' }}>
                      نمایش <strong>{range[0]}</strong> تا <strong>{range[1]}</strong> از <strong>{total}</strong> محصول
                    </span>
                  ),
                  locale: {
                    items_per_page: 'ردیف در صفحه',
                  },
                }}
                onChange={handleTableChange}
                locale={{
                  emptyText: (
                    <div style={{ fontFamily: 'CustomFont', padding: '40px 0', textAlign: 'center', color: '#999' }}>
                      {filters.search ? 'نتیجه‌ای یافت نشد' : 'هنوز محصولی ثبت نشده است'}
                    </div>
                  ),
                }}
                className="custom-table"
                style={{ fontFamily: 'CustomFont' }}
                rowClassName="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                scroll={{ x: 1200 }}
              />
            </div>
              </>
            )}
          </div>
        </Card>
      </div>

      <style jsx>{`
        :global(.custom-table .ant-table-thead > tr > th) {
          background: linear-gradient(to bottom, #fafafa, #f0f0f0) !important;
          font-weight: 600 !important;
          border-bottom: 2px solid #e8e8e8 !important;
        }
        :global(.dark .custom-table .ant-table-thead > tr > th) {
          background: linear-gradient(to bottom, #1f1f1f, #262626) !important;
          border-bottom: 2px solid #434343 !important;
        }
        :global(.custom-table .ant-table-tbody > tr:hover > td) {
          background: #f5f5f5 !important;
        }
        :global(.dark .custom-table .ant-table-tbody > tr:hover > td) {
          background: #262626 !important;
        }
      `}</style>
    </MainLayout>
  );
};

export default Products;
